import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap, filter, take } from 'rxjs/operators';
import { ConfigService } from './config.service';

export interface MapboxPlace {
  id: string;
  type: string;
  place_type: string[];
  relevance: number;
  properties: {
    foursquare?: string;
    landmark?: boolean;
    address?: string;
    category?: string;
    maki?: string;
    tel?: string;
    website?: string;
  };
  text: string;
  place_name: string;
  center: number[]; // [longitude, latitude]
  geometry: {
    type: string;
    coordinates: number[];
  };
  context?: Array<{
    id: string;
    short_code?: string;
    wikidata?: string;
    text: string;
  }>;
}

export interface MapboxResponse {
  type: string;
  query: string[];
  features: MapboxPlace[];
  attribution: string;
}

export interface RestaurantResult {
  id: string;
  name: string;
  category: string;
  address: string;
  location: {
    lat: number;
    lng: number;
  };
  distance?: number;
  phone?: string;
  website?: string;
  relevance: number;
  foursquareId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class MapboxService {
  private readonly BASE_URL = 'https://api.mapbox.com';
  private accessToken = '';

  // Categorías de lugares para comer
  private readonly FOOD_CATEGORIES = [
    'restaurant',
    'cafe',
    'bar',
    'fast_food',
    'food_and_drink',
    'nightlife',
    'bakery'
  ];

  /** Observable that emits the token once config is loaded */
  private token$: Observable<string>;

  constructor(private http: HttpClient, private configService: ConfigService) {
    // Wait for config to load, then extract the mapbox token
    this.token$ = this.configService.config$.pipe(
      filter(config => config !== null),
      take(1),
      map(config => {
        const token = config?.mapbox?.enabled ? config.mapbox.accessToken : '';
        this.accessToken = token;
        return token;
      })
    );

    // Eagerly subscribe to set the token as soon as config arrives
    this.token$.subscribe();
  }

  /**
   * Helper: waits for the token then executes the request
   */
  private withToken<T>(fn: (token: string) => Observable<T>, fallback: T): Observable<T> {
    // If token is already available, use it directly
    if (this.accessToken) {
      return fn(this.accessToken);
    }
    // Otherwise wait for config to load
    return this.token$.pipe(
      switchMap(token => {
        if (!token) {
          console.warn('Mapbox: no access token configured');
          return of(fallback);
        }
        return fn(token);
      })
    );
  }

  /**
   * Buscar restaurantes cerca de una ubicación
   */
  searchNearbyRestaurants(lat: number, lng: number, options?: {
    radius?: number;
    limit?: number;
    category?: string;
    query?: string;
  }): Observable<RestaurantResult[]> {
    return this.withToken<RestaurantResult[]>(token => {
      const categories = options?.category ? [options.category] : this.FOOD_CATEGORIES;
      const categoryString = categories.join(',');

      let params = new HttpParams()
        .set('access_token', token)
        .set('proximity', `${lng},${lat}`)
        .set('limit', Math.min(options?.limit || 10, 10).toString())
        .set('language', 'es')
        .set('country', 'ES');

      if (options?.radius) {
        const radiusKm = Math.min(options.radius / 1000, 50);
        params = params.set('bbox', this.createBboxFromRadius(lat, lng, radiusKm));
      }

      const searchQuery = options?.query || categoryString;

      return this.http.get<MapboxResponse>(`${this.BASE_URL}/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json`, {
        params
      }).pipe(
        map(response => this.transformToRestaurantResults(response.features, lat, lng)),
        catchError(error => {
          console.error('Mapbox search error:', error);
          return of([]);
        })
      );
    }, []);
  }

  /**
   * Buscar restaurantes por texto
   */
  searchRestaurants(query: string, options?: {
    lat?: number;
    lng?: number;
    limit?: number;
    country?: string;
  }): Observable<RestaurantResult[]> {
    return this.withToken<RestaurantResult[]>(token => {
      let params = new HttpParams()
        .set('access_token', token)
        .set('limit', Math.min(options?.limit || 10, 10).toString())
        .set('language', 'es')
        .set('types', 'poi');

      if (options?.country) {
        params = params.set('country', options.country.toUpperCase());
      }

      if (options?.lat && options?.lng) {
        params = params.set('proximity', `${options.lng},${options.lat}`);
      }

      const searchQuery = `${query} restaurant`;

      return this.http.get<MapboxResponse>(`${this.BASE_URL}/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json`, {
        params
      }).pipe(
        map(response => this.transformToRestaurantResults(
          response.features,
          options?.lat,
          options?.lng
        )),
        catchError(error => {
          console.error('Mapbox search error:', error);
          return of([]);
        })
      );
    }, []);
  }

  /**
   * Geocoding básico con Mapbox
   */
  geocode(address: string, options?: {
    proximity?: { lat: number; lng: number };
    country?: string;
    limit?: number;
  }): Observable<any[]> {
    return this.withToken<any[]>(token => {
      let params = new HttpParams()
        .set('access_token', token)
        .set('limit', (options?.limit || 5).toString())
        .set('language', 'es');

      if (options?.proximity) {
        params = params.set('proximity', `${options.proximity.lng},${options.proximity.lat}`);
      }

      if (options?.country) {
        params = params.set('country', options.country.toUpperCase());
      }

      return this.http.get<MapboxResponse>(`${this.BASE_URL}/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`, {
        params
      }).pipe(
        map(response => response.features.map(feature => ({
          lat: feature.center[1],
          lng: feature.center[0],
          address: feature.place_name,
          formattedAddress: feature.text,
          place_id: feature.id,
          confidence: feature.relevance
        }))),
        catchError(error => {
          console.error('Mapbox geocoding error:', error);
          return of([]);
        })
      );
    }, []);
  }

  /**
   * Autocompletar lugares
   */
  autocomplete(query: string, options?: {
    proximity?: { lat: number; lng: number };
    types?: string[];
    country?: string;
  }): Observable<any[]> {
    if (query.length < 2) {
      return of([]);
    }

    return this.withToken<any[]>(token => {
      let params = new HttpParams()
        .set('access_token', token)
        .set('limit', '8')
        .set('language', 'es')
        .set('autocomplete', 'true');

      if (options?.proximity) {
        params = params.set('proximity', `${options.proximity.lng},${options.proximity.lat}`);
      }

      if (options?.types && options.types.length > 0) {
        params = params.set('types', options.types.join(','));
      }

      if (options?.country) {
        params = params.set('country', options.country.toUpperCase());
      }

      return this.http.get<MapboxResponse>(`${this.BASE_URL}/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`, {
        params
      }).pipe(
        map(response => response.features.map(feature => ({
          id: feature.id,
          text: feature.text,
          place_name: feature.place_name,
          center: {
            lat: feature.center[1],
            lng: feature.center[0]
          },
          relevance: feature.relevance
        }))),
        catchError(error => {
          console.error('Mapbox autocomplete error:', error);
          return of([]);
        })
      );
    }, []);
  }

  /**
   * Configurar token de acceso
   */
  setAccessToken(token: string): void {
    this.accessToken = token;
  }

  /**
   * Transformar features de Mapbox a resultados de restaurantes
   */
  private transformToRestaurantResults(features: MapboxPlace[], userLat?: number, userLng?: number): RestaurantResult[] {
    return features
      .filter(feature => this.isRestaurantFeature(feature))
      .map(feature => {
        const result: RestaurantResult = {
          id: feature.id,
          name: feature.text,
          category: this.extractCategory(feature),
          address: feature.place_name,
          location: {
            lat: feature.center[1],
            lng: feature.center[0]
          },
          relevance: feature.relevance,
          phone: feature.properties?.tel,
          website: feature.properties?.website,
          foursquareId: feature.properties?.foursquare
        };

        if (userLat && userLng) {
          result.distance = this.calculateDistance(
            userLat, userLng,
            result.location.lat, result.location.lng
          );
        }

        return result;
      })
      .sort((a, b) => {
        if (a.distance !== undefined && b.distance !== undefined) {
          return a.distance - b.distance;
        }
        return (b.relevance || 0) - (a.relevance || 0);
      });
  }

  private isRestaurantFeature(feature: MapboxPlace): boolean {
    const category = feature.properties?.category?.toLowerCase() || '';
    const maki = feature.properties?.maki?.toLowerCase() || '';
    const text = feature.text.toLowerCase();

    const foodKeywords = [
      'restaurant', 'cafe', 'bar', 'food', 'pizza', 'burger',
      'coffee', 'bakery', 'bistro', 'tavern', 'pub', 'diner',
      'kitchen', 'grill', 'eatery', 'cafeteria'
    ];

    return this.FOOD_CATEGORIES.some(cat =>
      category.includes(cat) ||
      maki.includes(cat)
    ) || foodKeywords.some(keyword =>
      text.includes(keyword) ||
      category.includes(keyword)
    );
  }

  private extractCategory(feature: MapboxPlace): string {
    const category = feature.properties?.category?.toLowerCase() || '';
    const maki = feature.properties?.maki?.toLowerCase() || '';

    if (category.includes('restaurant') || maki.includes('restaurant')) return 'Restaurante';
    if (category.includes('cafe') || maki.includes('cafe')) return 'Cafetería';
    if (category.includes('bar') || maki.includes('bar')) return 'Bar';
    if (category.includes('fast_food')) return 'Comida rápida';
    if (category.includes('bakery')) return 'Panadería';

    return 'Restaurante';
  }

  private createBboxFromRadius(lat: number, lng: number, radiusKm: number): string {
    const latOffset = radiusKm / 111;
    const lngOffset = radiusKm / (111 * Math.cos(lat * Math.PI / 180));

    const minLng = lng - lngOffset;
    const minLat = lat - latOffset;
    const maxLng = lng + lngOffset;
    const maxLat = lat + latOffset;

    return `${minLng},${minLat},${maxLng},${maxLat}`;
  }

  private calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371e3;
    const p1 = lat1 * Math.PI / 180;
    const p2 = lat2 * Math.PI / 180;
    const dp = (lat2 - lat1) * Math.PI / 180;
    const dl = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(dp / 2) * Math.sin(dp / 2) +
              Math.cos(p1) * Math.cos(p2) *
              Math.sin(dl / 2) * Math.sin(dl / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  checkService(): Observable<boolean> {
    return this.withToken<boolean>(token => {
      return this.http.get(`${this.BASE_URL}/geocoding/v5/mapbox.places/test.json?access_token=${token}`)
        .pipe(
          map(() => true),
          catchError(() => of(false))
        );
    }, false);
  }
}
